import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import HistoryEntry from '../history/HistoryEntry';
import { errorText } from '../../lib/apiErrors';
import { channelLabel } from '../../lib/eventOutreachActions';
import { formatEventWhen } from '../../lib/eventWorkspace';
import { getEventAttendeeHistory } from '../../lib/historyApi';
import { historyItems as normalizeHistoryItems } from '../../lib/historyDisplay';
import { FORMER_MEMBER, personLabel } from '../../lib/teammateLabels';
import { formatDateInZone } from '../../lib/timezone';
import {
    EventChips, NextFollowUp, eventDetailHref, eventsCountLabel, eventsUnavailable, followLink, studioHref, timingOf,
} from './ContactEventsRail';

const HISTORY_LIMIT = 20;
const ACTION = 'px-2.5 py-1 text-xs font-medium rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed';

function Field({ label, testId, children }) {
    return (
        <div className="min-w-0">
            <dt className="text-[10px] font-semibold tracking-wider uppercase text-stone-400">{label}</dt>
            <dd className="m-0 text-xs break-words text-stone-700" data-testid={testId}>{children}</dd>
        </div>
    );
}

function lastOutreachText(row, zone) {
    const at = row.last_outreach?.at || row.last_outreach_at;
    if (!at) return 'None yet';
    const date = formatDateInZone(at, { zone }) || String(at);
    const channel = row.last_outreach?.channel;
    return channel ? `${channelLabel(channel)} · ${date}` : date;
}

// The server names a coach only while they are a member of the org; an id with no name is a former member.
const coachLabel = (contact) => personLabel(contact?.coach_assigned_user_id, contact?.coach_assigned_name);

const contributorsText = (row) => (row.contributors?.length
    ? row.contributors.map((person) => personLabel(person.id, person.name) || FORMER_MEMBER).join(', ')
    : 'None yet');

// The per-card history of this contact on this event (#891), read only once the card's history is opened.
function CardHistory({ eventId, contactId, version, zone, getAuthHeader }) {
    const [state, setState] = useState({ status: 'loading', items: [], error: null });
    const [attempt, setAttempt] = useState(0);
    const authRef = useRef(getAuthHeader);
    useEffect(() => { authRef.current = getAuthHeader; });
    useEffect(() => {
        let cancelled = false;
        setState((prev) => ({ ...prev, status: 'loading', error: null }));
        getEventAttendeeHistory(eventId, contactId, { limit: HISTORY_LIMIT }, authRef.current())
            .then((data) => { if (!cancelled) setState({ status: 'ready', items: normalizeHistoryItems(data), error: null }); })
            .catch((err) => { if (!cancelled) setState({ status: 'error', items: [], error: errorText(err, 'Failed to load history') }); });
        return () => { cancelled = true; };
    }, [eventId, contactId, version, attempt]);
    return (
        <div className="mt-2 space-y-2" data-testid="event-history">
            {state.status === 'loading' && <p className="text-xs text-stone-400">Loading history…</p>}
            {state.status === 'error' && (
                <p className="text-xs text-stone-500">{state.error} <button type="button" className="font-medium underline" onClick={() => setAttempt((n) => n + 1)} data-testid="event-history-retry">Retry</button></p>
            )}
            {state.status === 'ready' && state.items.length === 0 && <p className="text-xs text-stone-400">No history recorded yet.</p>}
            {state.status === 'ready' && state.items.map((item, index) => (
                <HistoryEntry key={item.id || index} item={item} zone={zone} compact />
            ))}
            {/* The read is the latest HISTORY_LIMIT entries and carries no "more" marker: a full page may be all there is. */}
            {state.status === 'ready' && state.items.length >= HISTORY_LIMIT && (
                <p className="text-[11px] text-stone-400" data-testid="event-history-limit">{`Showing the latest ${HISTORY_LIMIT} entries.`}</p>
            )}
        </div>
    );
}

function EventCard({
    row, contactId, ownerLabel, coachName, zone, getAuthHeader, canAct, actReason, canOpenEvent, onNavigate, onLogCall, onSetFollowUp,
}) {
    const eventId = row.event.event_id;
    const past = row.tab === 'past';
    const [open, setOpen] = useState(!past);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const href = eventDetailHref(row, contactId, canOpenEvent);
    const name = row.event.name || 'Untitled event';
    const when = formatEventWhen(timingOf(row));
    const cardClass = 'rounded-lg border border-stone-200 bg-white';

    if (!open) {
        return (
            <article className={cardClass} data-testid="contact-event-card" data-event-id={eventId} data-collapsed="true">
                <button type="button" onClick={() => setOpen(true)} aria-expanded="false" data-testid="contact-event-summary-toggle"
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 w-full text-left">
                    <ChevronRight className="w-3.5 h-3.5 shrink-0 text-stone-400" />
                    <span className="text-sm font-medium truncate text-stone-800">{name}</span>
                    <span className="text-xs text-stone-500">{when}</span>
                    <EventChips row={row} />
                </button>
            </article>
        );
    }

    const logCall = async () => {
        setBusy(true);
        try { await onLogCall(row); } finally { setBusy(false); }
    };

    return (
        <article className={`${cardClass} p-3 sm:p-4`} data-testid="contact-event-card" data-event-id={eventId} data-collapsed="false">
            <div className="flex gap-2 justify-between items-start">
                <a href={href} onClick={(event) => followLink(event, href, onNavigate)} className="min-w-0 hover:underline" data-testid="event-card-link">
                    <span className="block text-sm font-semibold truncate text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{name}</span>
                    <span className="block text-xs text-stone-500" data-testid="event-card-when">{when}</span>
                </a>
                {past && (
                    <button type="button" onClick={() => setOpen(false)} className="text-xs text-stone-500 hover:text-stone-700 shrink-0" aria-expanded="true" data-testid="contact-event-collapse">Collapse</button>
                )}
            </div>
            <div className="mt-2"><EventChips row={row} /></div>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 mt-3 sm:grid-cols-2">
                <Field label="Contact Owner" testId="event-contact-owner">{ownerLabel || 'No Contact Owner'}</Field>
                <Field label="Assigned Coach" testId="event-assigned-coach">{coachName || 'No Assigned Coach'}</Field>
                <Field label="Contributors" testId="event-contributors">{contributorsText(row)}</Field>
                <Field label="Last outreach" testId="event-last-outreach">{lastOutreachText(row, zone)}</Field>
                <div className="sm:col-span-2">
                    <Field label="Next follow-up" testId="event-follow-up-field"><NextFollowUp followUp={row.next_follow_up} zone={zone} detailed /></Field>
                </div>
            </dl>
            <div className="flex flex-wrap gap-2 mt-3">
                <button type="button" onClick={logCall} disabled={!canAct || busy} title={!canAct ? actReason : undefined} className={ACTION} data-testid="event-log-call">
                    {busy ? 'Opening…' : 'Log call'}
                </button>
                <button type="button" onClick={() => onSetFollowUp(row)} disabled={!canAct} title={!canAct ? actReason : undefined} className={ACTION} data-testid="event-set-follow-up">Set follow-up</button>
                <button type="button" onClick={() => onNavigate(studioHref(row, contactId))} disabled={!canAct} title={!canAct ? actReason : undefined} className={ACTION} data-testid="event-open-studio">Open Outreach Studio</button>
            </div>
            <div className="mt-3 pt-2 border-t border-stone-100">
                <button type="button" onClick={() => setHistoryOpen((v) => !v)} aria-expanded={historyOpen}
                    className="flex gap-1 items-center text-xs font-medium text-stone-600 hover:text-stone-800" data-testid="event-history-toggle">
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
                    {historyOpen ? 'Hide history' : 'Show history'}
                </button>
                {historyOpen && <CardHistory eventId={eventId} contactId={contactId} version={row.state_version} zone={zone} getAuthHeader={getAuthHeader} />}
            </div>
        </article>
    );
}

/**
 * Contact Detail's Events tab (#889, D2): one card per event this contact is on, from the one shared
 * `useContactEvents` read. Owner and coach are labels only: the Contact Owner and Assigned Coach change in
 * their own controls, never here. A card's actions go through the page's existing quick-log, follow-up and
 * Studio flows (`onLogCall`, `onSetFollowUp`, `onNavigate`), and the browser writes no event state itself.
 */
export default function ContactEventsTab({
    events, contactId, contact, ownerLabel, zone, getAuthHeader, canAct, actReason, canOpenEvent,
    onNavigate, onLogCall, onSetFollowUp, onAddToEvent,
}) {
    const { rows, loading, loadingMore, error, loadMoreError, nextCursor, loadMore, reload } = events;
    const unavailable = eventsUnavailable(events);
    let body;
    if (unavailable) {
        body = <p className="py-8 text-sm text-center text-stone-500" data-testid="contact-events-tab-unavailable">Events are not available for this contact.</p>;
    } else if (error && rows.length === 0) {
        body = (
            <div className="py-8 text-center" data-testid="contact-events-tab-error">
                <p className="text-sm text-stone-500">{error.message}</p>
                <button type="button" onClick={reload} className="mt-2 text-sm font-medium text-stone-700 underline" data-testid="contact-events-tab-retry">Retry</button>
            </div>
        );
    } else if (loading && rows.length === 0) {
        body = (
            <div className="space-y-3 animate-pulse" data-testid="contact-events-tab-skeleton" aria-busy="true">
                <div className="h-40 rounded-lg bg-stone-100" />
                <div className="h-40 rounded-lg bg-stone-100" />
            </div>
        );
    } else if (rows.length === 0) {
        body = <p className="py-8 text-sm text-center text-stone-400" data-testid="contact-events-tab-empty">Not on any event yet</p>;
    } else {
        body = (
            <>
                {error && (
                    <p className="text-xs text-stone-500">Could not refresh. <button type="button" onClick={reload} className="font-medium underline" data-testid="contact-events-tab-retry">Retry</button></p>
                )}
                {rows.map((row) => (
                    <EventCard key={row.attendee_id || row.event.event_id} row={row} contactId={contactId}
                        ownerLabel={ownerLabel} coachName={coachLabel(contact)} zone={zone} getAuthHeader={getAuthHeader}
                        canAct={canAct} actReason={actReason} canOpenEvent={canOpenEvent} onNavigate={onNavigate}
                        onLogCall={onLogCall} onSetFollowUp={onSetFollowUp} />
                ))}
                {nextCursor && (
                    <div className="text-center">
                        <button type="button" onClick={loadMore} disabled={loadingMore} className={ACTION} data-testid="events-load-more">
                            {loadingMore ? <span className="inline-flex gap-1 items-center"><Loader2 className="w-3 h-3 animate-spin" />Loading…</span> : 'Load more'}
                        </button>
                        {loadMoreError && <p className="mt-1 text-xs text-red-600" data-testid="events-load-more-error">{loadMoreError.message}</p>}
                    </div>
                )}
            </>
        );
    }
    return (
        <div className="p-4 space-y-3 bg-white rounded-xl border border-stone-200 sm:p-5" data-testid="contact-events-tab">
            <div className="flex gap-2 justify-between items-center">
                <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Events ({eventsCountLabel(events)})</h3>
                {!unavailable && <button type="button" onClick={onAddToEvent} className={ACTION} data-testid="events-add-to-event">+ Add to another event</button>}
            </div>
            {body}
        </div>
    );
}
